<script setup lang="ts">
/**
 * "添加到主屏幕" 引导横幅。
 *
 * 触发条件: 移动 iOS Safari + 当前不在 standalone 模式 + 用户从未关闭过。
 * (Android Chrome 的 PWA 安装由浏览器自动 prompt, 不需要这条引导。)
 *
 * 关闭后写入 localStorage, 同一台设备不再展示。
 */

import { computed, ref, onMounted } from 'vue'

import { useStandalone } from '@/composables/useStandalone'

const { isStandalone, isIOSSafari } = useStandalone()

const STORAGE_KEY = 'install_hint_dismissed_at'
const SHOW_AFTER_MS = 1500    // 进入页面 1.5s 后再淡入, 不打扰刚加载

const dismissed = ref(true)   // 默认不显示, 等 onMounted 决定
const visible   = ref(false)

const shouldShow = computed(() =>
  !isStandalone.value
  && isIOSSafari.value
  && !dismissed.value,
)

onMounted(() => {
  const saved = localStorage.getItem(STORAGE_KEY)
  dismissed.value = !!saved
  if (shouldShow.value) {
    setTimeout(() => { visible.value = true }, SHOW_AFTER_MS)
  }
})

function dismiss() {
  visible.value = false
  dismissed.value = true
  localStorage.setItem(STORAGE_KEY, Date.now().toString())
}
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-3"
    leave-active-class="transition duration-200"
    leave-to-class="opacity-0 translate-y-3"
  >
    <div
      v-if="shouldShow && visible"
      class="fixed left-3 right-3 z-40 mx-auto max-w-md
             bg-neutral-900/95 border border-gold/40 rounded-xl
             shadow-2xl shadow-black/60 backdrop-blur-md
             px-4 py-3 flex items-start gap-3"
      :style="{ bottom: 'max(0.75rem, env(safe-area-inset-bottom))' }"
      role="status"
      aria-live="polite"
    >
      <div class="text-2xl pt-0.5">📲</div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-bold text-gold-lt mb-1">添加到主屏幕</p>
        <p class="text-xs text-neutral-300 leading-relaxed">
          点击底部
          <span
            class="inline-flex items-center px-1.5 py-0.5 mx-0.5
                   rounded bg-white/10 border border-white/15
                   text-[10px] font-bold align-middle"
          >分享 ⬆</span>
          → 选择
          <span
            class="inline-flex items-center px-1.5 py-0.5 mx-0.5
                   rounded bg-white/10 border border-white/15
                   text-[10px] font-bold align-middle"
          >添加到主屏幕</span>
          可获得无 URL 栏的全屏游戏体验。
        </p>
      </div>
      <button
        type="button"
        class="flex-shrink-0 -mr-1 -mt-1 p-2 text-neutral-500 hover:text-neutral-200
               transition text-xl leading-none tap-target"
        aria-label="Dismiss"
        @click="dismiss"
      >×</button>
    </div>
  </Transition>
</template>
